// ============================================================
// TUTORIAL CONFIGURATION
// Generated by Learn by Building Tutorial Generator
// Date: 2026-01-19
// Source: Video recording - "Import a QSF Survey File"
// ============================================================

// ------------------------------------------------------------
// TUTORIAL METADATA
// ------------------------------------------------------------
const tutorialConfig = {
  id: 'import-qsf-survey',
  title: 'Import a QSF Survey File',
  description: 'Learn how to export an existing survey as a QSF file and import it to create a new project in Qualtrics.',
  estimatedTime: '3 minutes',
  difficulty: 'Beginner',
  prerequisites: [],
  tags: ['survey', 'import', 'export', 'qsf', 'project-creation', 'backup'],
  brandColor: '#0066CC',
};

// ------------------------------------------------------------
// TUTORIAL STEPS
// ------------------------------------------------------------
const steps = [
  {
    id: 1,
    title: 'Let\'s export this survey',
    instruction: 'From the Survey Builder, we need to export this as a QSF file to use it in a different account',
    screenshot: '/screenshots/import-qsf-survey/step-1.png',
    hotspot: {
      top: 17.6,
      left: 22.5,
      width: 5,
      height: 8.5,
    },
    hint: 'Look for the "Tools" button with a dropdown arrow, located near the save indicator',
    wrongClickMessage: 'Not quite! Look for the "Tools" dropdown button in the toolbar near the top of the screen.',
    requiresInput: false,
  },
  {
    id: 2,
    title: 'Select the option to export the survey as a QSF file',
    instruction: 'From the Import/Export submenu, click the option to download your survey as a QSF file.',
    screenshot: '/screenshots/import-qsf-survey/step-3.png',
    hotspot: {
      top: 53.8,
      left: 33.6,
      width: 10,
      height: 4,
    },
    hint: 'In the submenu that appeared, find and click "Export survey" - this will download your survey as a QSF file.',
    wrongClickMessage: 'Not quite! Look for "Export survey" in the submenu that appeared to the right.',
    requiresInput: false,
  },
  {
    id: 3,
    title: 'Now, let\'s go to the project where we want to use this file',
    instruction: 'Navigate to your projects',
    screenshot: '/screenshots/import-qsf-survey/step-4.png',
    hotspot: {
      top: 18,
      left: 4.7,
      width: 14,
      height: 8.5,
    },
    hint: 'Find "Projects" in the navigation menu - it\'s on the top, below "Home".',
    wrongClickMessage: 'That\'s not the Projects option. Look for "Projects" in the left navigation menu.',
    requiresInput: false,
  },
  {
    id: 4,
    title: 'Let\'s create a new and clean project',
    instruction: 'Let\'s use this QSF file you downloaded from a different project',
    screenshot: '/screenshots/import-qsf-survey/step-5.png',
    hotspot: {
      top: 13.3,
      left: 91.8,
      width: 7.5,
      height: 8.5,
    },
    hint: 'Look for the blue "Create project" button in the top-right corner of the Projects page.',
    wrongClickMessage: 'Not quite! Look for the blue "Create project" button in the top-right corner.',
    requiresInput: false,
  },
  {
    id: 5,
    title: 'Create a new survey project',
    instruction: 'Select the correct option to create a new survey project from the catalog',
    screenshot: '/screenshots/import-qsf-survey/step-7.png',
    hotspot: {
      top: 27.9,
      left: 20.8,
      width: 15,
      height: 8,
    },
    hint: 'The Survey option is the first option in the "From scratch" section, with a clipboard icon.',
    wrongClickMessage: 'That\'s not the Survey tile. Look in the "From scratch" section for the first tile labeled "Survey".',
    requiresInput: false,
  },
  {
    id: 6,
    title: 'Let\'s get started',
    instruction: 'Proceed with the project creation',
    screenshot: '/screenshots/import-qsf-survey/step-8.png',
    hotspot: {
      top: 87.5,
      left: 80.6,
      width: 18,
      height: 10,
    },
    hint: 'Look for the blue "Get started" button at the bottom right of the screen.',
    wrongClickMessage: 'Almost! Find the blue "Get started" button in the bottom-right corner of the panel.',
    requiresInput: false,
  },
  {
    id: 7,
    title: 'Enter a project name and click "Enter"',
    instruction: 'In the "Create a new project" dialog, type a name for your imported survey in the Name field (e.g., "Q pizza").',
    screenshot: '/screenshots/import-qsf-survey/step-9.png',
    hotspot: {
      top: 48,
      left: 41,
      width: 17,
      height: 3.5,
    },
    hint: 'Click in the Name text field and type a descriptive name for your project and click "enter".',
    wrongClickMessage: 'Click in the Name input field to enter your project name.',
    requiresInput: true,
    inputPlaceholder: 'Q pizza',
    inputValidation: /^.{1,}$/,
    inputField: {
      top: 48,
      left: 41,
      width: 17,
      height: 3.5,
    },
  },
  {
    id: 8,
    title: 'Select the option to import the file you exported',
    instruction: 'From the dropdown options, select the option to import your previously exported survey.',
    screenshot: '/screenshots/import-qsf-survey/step-10.png',
    hotspot: {
      top: 62.6,
      left: 41.2,
      width: 17,
      height: 6,
    },
    hint: 'Look for "Import a QSF file" option in the dropdown menu.',
    wrongClickMessage: 'That\'s not the right option. Find and click "Import a QSF file" in the dropdown list.',
    requiresInput: false,
  },
  {
    id: 9,
    title: 'Let\'s select the file!',
    instruction: 'Select the file you exported from a different project',
    screenshot: '/screenshots/import-qsf-survey/step-11.png',
    hotspot: {
      top: 57.9,
      left: 40.9,
      width: 6,
      height: 6,
    },
    hint: 'Look for the "Choose file" button below the dropdown to select your QSF file.',
    wrongClickMessage: 'Not quite! Click the "Choose file" button to browse for your QSF file.',
    requiresInput: false,
  },
  {
    id: 10,
    title: 'Select your QSF file',
    instruction: 'Navigate to your Downloads folder and select the QSF file you exported earlier.',
    screenshot: '/screenshots/import-qsf-survey/step-13.png',
    hotspot: {
      top: 50,
      left: 19,
      width: 13,
      height: 2.5,
    },
    hint: 'Find the QSF file in your Downloads folder - it will have a .qsf extension.',
    wrongClickMessage: 'Select the correct QSF file from the list. Look for a file ending in .qsf.',
    requiresInput: false,
  },
  {
    id: 11,
    title: 'Click "Open"',
    instruction: 'With the QSF file selected, click the "Open" button to confirm your selection.',
    screenshot: '/screenshots/import-qsf-survey/step-14.png',
    hotspot: {
      top: 67,
      left: 84.7,
      width: 4.7,
      height: 6,
    },
    hint: 'Click the blue "Open" button in the bottom-right of the file picker.',
    wrongClickMessage: 'Click the "Open" button to confirm your file selection.',
    requiresInput: false,
  },
  {
    id: 12,
    title: 'Create the project!',
    instruction: 'Click the blue "Create project" button to import your survey and create the new project.',
    screenshot: '/screenshots/import-qsf-survey/step-15.png',
    hotspot: {
      top: 68,
      left: 41,
      width: 17,
      height: 3.5,
    },
    hint: 'Click the blue "Create project" button at the bottom to complete the import.',
    wrongClickMessage: 'Almost done! Click the blue "Create project" button to finish importing your survey.',
    requiresInput: false,
  },
];

// ------------------------------------------------------------
// SCREENSHOT CHECKLIST
// ------------------------------------------------------------
/*
Screenshots needed for this tutorial:

□ step-1.png: Survey Builder - initial view
  - Capture when: Viewing an existing survey in the Survey Builder
  - Key elements visible: Survey tab active, Tools dropdown, survey content
  - Resolution: Match your standard screenshot dimensions

□ step-2.png: Tools menu open
  - Capture when: After clicking Tools dropdown
  - Key elements visible: Dropdown menu showing all options including Import/Export
  - Note: Cursor should be near Import/Export

□ step-3.png: Import/Export submenu visible
  - Capture when: Hovering over Import/Export
  - Key elements visible: Submenu with Print survey, Import survey, Export survey options
  - Note: Export survey should be highlighted

□ step-4.png: Survey Builder with navigation menu open
  - Capture when: After clicking hamburger menu
  - Key elements visible: Navigation menu with Home, Projects, Scheduling, etc.

□ step-5.png: Navigation menu - Projects highlighted
  - Capture when: Hovering over Projects in nav menu
  - Key elements visible: Projects option highlighted

□ step-6.png: Projects page
  - Capture when: On the Projects and Programs page
  - Key elements visible: Project list, Create project button in top-right

□ step-7.png: Project catalog
  - Capture when: After clicking Create project
  - Key elements visible: "From scratch" section with Survey, Engagement, 360, etc. tiles

□ step-8.png: Survey tile selected
  - Capture when: After clicking Survey tile
  - Key elements visible: Survey tile highlighted, right panel with description, Get started button

□ step-9.png: Create new project modal - blank
  - Capture when: After clicking Get started
  - Key elements visible: Name field (empty or with default), dropdown set to "Create a blank survey project"

□ step-10.png: Dropdown open
  - Capture when: After clicking the dropdown
  - Key elements visible: All dropdown options including "Import a QSF file"

□ step-11.png: Import QSF option highlighted
  - Capture when: Hovering over "Import a QSF file"
  - Key elements visible: Import a QSF file option highlighted

□ step-12.png: Import QSF selected, Choose file visible
  - Capture when: After selecting Import a QSF file
  - Key elements visible: Choose file button, QSF explanation text

□ step-13.png: macOS file picker with QSF file
  - Capture when: File picker open, QSF file visible
  - Key elements visible: File list, QSF file highlighted, Open button

□ step-14.png: File selected, ready to open
  - Capture when: QSF file selected in picker
  - Key elements visible: File preview, Open button highlighted

□ step-15.png: Ready to create project
  - Capture when: File uploaded, ready to create
  - Key elements visible: Selected file shown, Create project button
*/

// ------------------------------------------------------------
// EXPORTS
// ------------------------------------------------------------
export { tutorialConfig, steps };
